import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAxios } from '../../../hooks/useAxios';
import { ApiCollection } from '../../../config/envConfig';
import './index.css';
import { setActiveUser } from '../../../redux/userSlice';
import { useDispatch } from 'react-redux';
import { LocalUser } from '../../../utils/interface/localUser';
import { useUser } from '../../../redux/useStore';

const StudentOnboardingScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const axios = useAxios();

  const [formData, setFormData] = useState({
    name: '',
    class: '',
    stream: '',
    mobileNumber: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [classes, setClasses] = useState<string[]>([]);
  const [streams, setStreams] = useState<string[]>([]);
  const user = useUser();

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(ApiCollection.user.getClasses);
        setClasses(response.data.available_classes);
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchStreams = async () => {
      if (!formData.class || formData.class === '10') {
        setStreams([]);
        return;
      }
      
      try {
        const response = await axios.get(
          `${ApiCollection.user.getStreams}?class=${formData.class}th`
        );
        setStreams(response.data.available_stream || []);
      } catch (error) {
        console.error('Error fetching streams:', error);
      }
    };

    fetchStreams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.class]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault ();
    setIsLoading(true);

    try {
      const updateData = {
        name: formData.name,
        mobile_number: formData.mobileNumber,
        educational_info: {
          class: `${formData.class}th`,
          stream: formData.stream,
        
        }
      };

      const response = await axios.put(ApiCollection.user.updateProfile, updateData, {
        headers: {
          Authorization: `Bearer ${user?.token}`
        }
      });


      if (!user) {
        throw new Error('No existing user data found');
      }

      const userInfo: LocalUser = {
        ...user,
        mobileNo: response.data.mobile_number ?? user.mobileNo,
        dateOfBirth: response.data.date_of_birth ?? user.dateOfBirth,
        email: response.data.email ?? user.email,
        educationalInfo: response.data.educational_info ? {
          class: response.data.educational_info.class,
          stream: response.data.educational_info.stream
        } : user.educationalInfo
      };

      await dispatch(setActiveUser(userInfo));
      

      navigate('/assessments', { replace: true });

    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding-wrapper">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h1 className="onboarding-heading">Student Onboarding</h1>
          <p className="onboarding-subtitle">Please complete your profile to continue</p>
        </div>

        <div className="onboarding-content">

          <form onSubmit={handleSubmit} className="onboarding-form">
            <div className="onboarding-form-group">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder=" "
              />
              <label htmlFor="name">Full Name</label>
              <div className="onboarding-form-line"></div>
            </div>

            <div className="onboarding-form-group">
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
                placeholder=" "
                pattern="[0-9]{10}"
              />
              <label htmlFor="mobileNumber">Mobile Number</label>
              <div className="onboarding-form-line"></div>
            </div>
            

            <div className="onboarding-form-group">
              <select
                id="class"
                name="class"
                value={formData.class}
                onChange={handleChange}
                required
                className={formData.class ? 'has-value' : ''}
              >
                <option value="" disabled>Select Class</option>
                {classes.map((classItem) => (
                  <option key={classItem} value={classItem.replace('th', '')}>
                    {classItem}
                  </option>
                ))}
              </select>
              <label htmlFor="class">Class</label>
              <div className="onboarding-form-line"></div>
            </div>


            {formData.class !== '10' && streams.length > 0 && (
              <div className="onboarding-form-group">
                <select
                  id="stream"
                  name="stream"
                  value={formData.stream}
                  onChange={handleChange}
                  required
                  className={formData.stream ? 'has-value' : ''}
                  disabled={!formData.class}
                >
                  <option value="" disabled>Select Stream</option>
                  {streams.map((stream) => (
                    <option key={stream} value={stream}>
                      {stream}
                    </option>
                  ))}
                </select>
                <label htmlFor="stream">Stream</label>
                <div className="onboarding-form-line"></div>
              </div>
            )}



            <button
              type="submit"
              className="onboarding-submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Submit'}
              {!isLoading && <span className="onboarding-button-icon">→</span>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StudentOnboardingScreen;